import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Target, LogOut, Plus, Pencil, Trash2, UploadCloud, Loader2, Lock, X, Search,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { adminApi, auth, suppliesApi, uploadImage, resolveImage } from "../api";

const CATEGORY_SUGGESTIONS = ["Flights", "Shafts", "Points", "Accessories", "Barrels", "Novelty", "Other"];

const inputCls =
  "bg-[#0f0f11] border-white/15 text-white placeholder:text-zinc-500 focus-visible:ring-[#ff2e4d]";

function Login({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { token } = await adminApi.login(password);
      auth.setToken(token);
      toast.success("Welcome back");
      onLogin();
    } catch (err) {
      toast.error("Incorrect password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-4">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0f0f11] p-8">
        <div className="flex items-center gap-2.5">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-[#ff2e4d]">
            <Target className="h-6 w-6 text-white" strokeWidth={2.4} />
          </span>
          <div>
            <div className="font-condensed text-xl text-white tracking-wide">ADMIN</div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#ff2e4d] font-semibold">Bulls on Parade</div>
          </div>
        </div>
        <h1 className="mt-6 font-display text-2xl font-bold text-white">Manage your Pro Shop</h1>
        <p className="mt-2 text-sm text-zinc-400">Enter your admin password to add or edit supplies.</p>
        <div className="mt-6 space-y-1.5">
          <Label className="text-sm text-zinc-300">Password</Label>
          <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="••••••••" autoFocus />
        </div>
        <button type="submit" disabled={loading} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#ff2e4d] px-6 py-3 text-sm font-semibold text-white hover:bg-[#e01f3c] transition-colors disabled:opacity-60">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Sign in
        </button>
      </form>
    </div>
  );
}

const emptyForm = { name: "", price: "$3/pack", brand: "", category: "Flights", image_url: "" };

function SupplyModal({ open, initial, onClose, onSaved }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setForm(initial ? { ...emptyForm, ...initial } : emptyForm);
  }, [initial, open]);

  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await uploadImage(file);
      setForm((f) => ({ ...f, image_url: url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        price: form.price.trim() || "$3/pack",
        brand: form.brand.trim(),
        category: form.category.trim() || "Other",
        image_url: form.image_url,
      };
      if (initial?.id) await suppliesApi.update(initial.id, payload);
      else await suppliesApi.create(payload);
      toast.success(initial?.id ? "Supply updated" : "Supply added");
      onSaved();
      onClose();
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg overflow-hidden rounded-2xl border border-white/10 bg-[#121214] shadow-2xl">
        <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
          <h3 className="font-display text-lg font-bold text-white">{initial?.id ? "Edit supply" : "Add supply"}</h3>
          <button onClick={onClose} className="rounded-md p-1 text-zinc-400 hover:bg-white/10 hover:text-white"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={save} className="max-h-[75vh] overflow-y-auto px-6 py-5 space-y-4">
          {/* Image */}
          <div>
            <Label className="text-sm text-zinc-300">Product photo</Label>
            <div className="mt-2 flex items-center gap-4">
              <div className="flex h-24 w-24 flex-none items-center justify-center overflow-hidden rounded-xl border border-white/10 bg-[#0a0a0c]">
                {form.image_url ? (
                  <img src={resolveImage(form.image_url)} alt="preview" className="h-full w-full object-contain p-2" />
                ) : (
                  <UploadCloud className="h-7 w-7 text-zinc-600" />
                )}
              </div>
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-white/15 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/10 transition-colors">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
                {uploading ? "Uploading…" : "Upload photo"}
                <input type="file" accept="image/*,.heic,.heif" className="hidden" onChange={onFile} />
              </label>
            </div>
            <p className="mt-2 text-xs text-zinc-500">Choose from your camera roll or take a new photo. iPhone (HEIC) and large photos are auto-converted.</p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm text-zinc-300">Name <span className="text-[#ff2e4d]">*</span></Label>
            <Input required value={form.name} onChange={set("name")} className={inputCls} placeholder="e.g. Shot Flight — Black/Gold" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-sm text-zinc-300">Price</Label>
              <Input value={form.price} onChange={set("price")} className={inputCls} placeholder="$3/pack" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm text-zinc-300">Brand</Label>
              <Input value={form.brand} onChange={set("brand")} className={inputCls} placeholder="e.g. Shot" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm text-zinc-300">Category</Label>
            <Input list="cat-suggestions" value={form.category} onChange={set("category")} className={inputCls} placeholder="Flights" />
            <datalist id="cat-suggestions">
              {CATEGORY_SUGGESTIONS.map((c) => <option key={c} value={c} />)}
            </datalist>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 rounded-lg border border-white/15 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/10 transition-colors">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-[#ff2e4d] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#e01f3c] transition-colors disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [query, setQuery] = useState("");
  const [confirmId, setConfirmId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await suppliesApi.list();
      setItems(data);
    } catch (err) {
      toast.error("Failed to load supplies");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    try {
      await suppliesApi.remove(id);
      toast.success("Deleted");
      setConfirmId(null);
      load();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const filtered = items.filter((i) =>
    !query || i.name.toLowerCase().includes(query.toLowerCase()) ||
    (i.brand || "").toLowerCase().includes(query.toLowerCase()) ||
    (i.category || "").toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-white">Pro Shop Manager</h1>
          <p className="mt-1 text-sm text-zinc-400">{items.length} items · add, edit or remove supplies. Changes are live.</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => { setEditing(null); setModalOpen(true); }} className="inline-flex items-center gap-2 rounded-lg bg-[#ff2e4d] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#e01f3c] transition-colors">
            <Plus className="h-4 w-4" /> Add supply
          </button>
          <button onClick={onLogout} className="inline-flex items-center gap-2 rounded-lg border border-white/15 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/10 transition-colors">
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>
      </div>

      <div className="relative mt-6 max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} className={`${inputCls} pl-9`} placeholder="Search by name, brand or category…" />
      </div>

      {loading ? (
        <div className="mt-16 flex items-center justify-center text-zinc-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : (
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {filtered.map((s) => (
            <div key={s.id} className="bop-card overflow-hidden rounded-xl">
              <div className="aspect-square overflow-hidden bg-[#0a0a0c]">
                {s.image_url ? (
                  <img src={resolveImage(s.image_url)} alt={s.name} loading="lazy" className="h-full w-full object-contain p-3" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-zinc-700"><Target className="h-8 w-8" /></div>
                )}
              </div>
              <div className="p-3">
                <h4 className="text-sm font-medium text-white leading-snug line-clamp-2">{s.name}</h4>
                <div className="mt-1 flex flex-wrap gap-1.5 text-[10px] text-zinc-400">
                  {s.brand && <span className="rounded bg-white/5 px-1.5 py-0.5">{s.brand}</span>}
                  {s.category && <span className="rounded bg-white/5 px-1.5 py-0.5">{s.category}</span>}
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-sm font-semibold text-[#ff2e4d]">{s.price}</span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => { setEditing(s); setModalOpen(true); }} className="rounded-md p-1.5 text-zinc-300 hover:bg-white/10 hover:text-white" title="Edit"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => setConfirmId(s.id)} className="rounded-md p-1.5 text-zinc-300 hover:bg-[#ff2e4d]/20 hover:text-[#ff2e4d]" title="Delete"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              </div>

              {confirmId === s.id && (
                <div className="border-t border-white/10 bg-[#1a0e11] px-3 py-2.5">
                  <p className="text-xs text-zinc-300">Delete this item?</p>
                  <div className="mt-2 flex gap-2">
                    <button onClick={() => del(s.id)} className="flex-1 rounded-md bg-[#ff2e4d] px-2 py-1 text-xs font-semibold text-white hover:bg-[#e01f3c]">Delete</button>
                    <button onClick={() => setConfirmId(null)} className="flex-1 rounded-md border border-white/15 px-2 py-1 text-xs font-semibold text-white hover:bg-white/10">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full py-16 text-center text-sm text-zinc-500">No supplies match your search.</div>
          )}
        </div>
      )}

      <SupplyModal open={modalOpen} initial={editing} onClose={() => setModalOpen(false)} onSaved={load} />
    </div>
  );
}

export default function Admin() {
  const [loggedIn, setLoggedIn] = useState(auth.isLoggedIn());

  useEffect(() => {
    if (auth.isLoggedIn()) {
      adminApi.verify().catch(() => { auth.clear(); setLoggedIn(false); });
    }
  }, []);

  const logout = () => { auth.clear(); setLoggedIn(false); toast.success("Logged out"); };

  return (
    <main className="bop-vignette min-h-[80vh]">
      {loggedIn ? <Dashboard onLogout={logout} /> : <Login onLogin={() => setLoggedIn(true)} />}
    </main>
  );
}
